<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-semibold text-gray-900">
        Project Groups
      </h3>

      <button
        type="button"
        class="btn btn--primary-blue"
        @click="showCreateModal = true"
      >
        Create Group
      </button>
    </div>

    <div
      v-if="indexProjectGroupsAction.loading.value"
      class="text-gray-500"
    >
      Loading...
    </div>

    <div
      v-else-if="projectGroups.length === 0"
      class="text-gray-500"
    >
      No project groups yet.
    </div>

    <div
      v-else
      class="space-y-3"
    >
      <div
        v-for="group in projectGroups"
        :key="group.id"
        class="border border-gray-200 rounded-lg p-4 flex items-center justify-between"
      >
        <div>
          <p class="font-semibold text-gray-900">
            {{ group.name }}
          </p>

          <p class="text-sm text-gray-600">
            {{ group.projects?.length ?? 0 }} projects · {{ group.shared_funds?.length ?? 0 }} shared funds · Budget {{ australianCurrency(totalSharedFundBudget(group)) }}
          </p>
        </div>

        <div class="flex items-center gap-2">
          <button
            type="button"
            class="btn btn--secondary-blue"
            @click="editGroup(group)"
          >
            Edit
          </button>

          <button
            type="button"
            class="btn btn--secondary-alert"
            @click="deleteGroup(group)"
          >
            Delete
          </button>
        </div>
      </div>
    </div>

    <ProjectGroupEditModal
      v-if="showCreateModal"
      :company-id="companyId"
      :project-group-id="selectedGroupId"
      @close="closeModal"
    />
  </div>
</template>

<script setup lang="ts">
import ProjectGroupEditModal from '@/components/ProjectSettings/CompanyTab/ProjectGroupEditModal.vue';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency } from '@/helpers';
import ProjectGroup from '@/models/ProjectGroup';
import { useProjectGroupsStore } from '@/store/projectGroups';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const props = defineProps<{ companyId: number }>();
const projectGroupsStore = useProjectGroupsStore();
const indexProjectGroupsAction = useStoreApiAction(projectGroupsStore.index);

watch(() => props.companyId, (companyId) => {
  indexProjectGroupsAction.request(companyId);
}, { immediate: true });

const totalSharedFundBudget = (group: ProjectGroup) => {
  return group.shared_funds?.reduce((total, fund) => total + (fund.budget ?? 0), 0) ?? 0;
};

const projectGroups = computed(() => {
  return projectGroupsStore.models
    .where('company_id', props.companyId)
    .with('projects')
    .with('shared_funds')
    .orderBy('name')
    .get();
});

const showCreateModal = ref(false);
const selectedGroupId = ref<Id>();

const editGroup = (group: ProjectGroup) => {
  showCreateModal.value = true;
  selectedGroupId.value = group.id;
};

const closeModal = () => {
  showCreateModal.value = false;
  selectedGroupId.value = undefined;
};

const deleteGroup = (group: ProjectGroup) => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete project group?',
    text: 'Projects will be removed from the group but not deleted.',
    showCancelButton: true,
  }).then((result) => {
    if(!result.isConfirmed) {
      return;
    }

    projectGroupsStore.delete(group.id);
  });
};
</script>
